import { timeout } from "@q/safeUtil";
import {
  callClient,
  getLoginInfo,
  getSystemInfo,
  getUserHeadLocalImage,
} from "@q/softCore";
import axios from "axios";
import jsonp, { jsonp2 } from "../utils/jsonp";
import { ICommentType, ILoginData, IRes, IComData } from "./interface";
import * as URL from "./url";

export const IS_CLIENT = !!(window.external && window.external.AppCmd);

if (!IS_CLIENT) {
  // debug
  window.external.AppCmd = function () {
    // test
  };
}

const getTimestamp = () => new Date().getTime();

// 默认配置
const DEFAULT_OPTION = {
  withCredentials: true,
  timeout: 20000,
};

const DEFAULT_PROMISE = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    getSystemInfo().then(resolve);
    timeout(2000).then(reject);
    resolve({
      mid: "123123123123",
      m2: "test_m2",
      osVer: "Windows+7",
      osbit: "64",
      ieVer: "",
      safe_type: "1", // 1 个人版安全卫士（目前分为团队版跟个人版）
      GT_safe: "",
    });
  }).catch(() => ({
    mid: "123123123123",
    m2: "test_m2",
    osVer: "Windows+7",
    osbit: "64",
    ieVer: "",
    safe_type: "1", // 1 个人版安全卫士（目前分为团队版跟个人版）
    GT_safe: "", /// 1 代表极速版，非1代表正常版本
  }));
};

// 抽象请求
const request =
  <T = IRes>(url: string) =>
  (data = {}, option = { method: "GET" }) => {
    const params = option.method.toUpperCase() !== "GET" ? "data" : "params";
    return axios({
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      url,
      [params]: data,
      ...DEFAULT_OPTION,
      ...option,
    }).then((res) => res.data as T);
  };

const requestJsonp =
  (url: string) =>
  (data = {}, option = {}) => {
    return jsonp({
      url,
      data,
      ...DEFAULT_OPTION,
      ...option,
    });
  };

const requestJsonp2 =
  (url: string) =>
  (data = {}, option = {}) => {
    return jsonp2({
      url,
      data,
      ...DEFAULT_OPTION,
      ...option,
    });
  };

/**
 * 获取首页数据
 *
 */
export function getIndexData() {
  if (IS_CLIENT) {
    return DEFAULT_PROMISE().then(({ GT_safe }) => {
      return callClient("requestPageInfo", "home")
        .then((res) => JSON.parse(res.home))
        .catch(() =>
          request(+GT_safe === 1 ? URL.jisuIndexData : URL.indexData)(),
        ); // 存在历史版本客户端对json解析失败问题，此时前端直接走线上请求
    });
  }
  return request(URL.indexData)();
}

/**
 * 开屏广告
 * @param {Object} data
 * @param {string} data.ad_type
 */
export function getScreenAd(data: { ad_type: string }) {
  return DEFAULT_PROMISE().then(({ mid, m2 = "", safe_type = "", GT_safe }) => {
    // 卫士极速版屏蔽开屏广告
    if (+GT_safe === 1) {
      return {
        errno: 1,
      };
    }

    return requestJsonp(URL.screenAd)({
      ...data,
      mid,
      m2,
      safe_type,
    });
  });
}

/**
 * 搜索结果
 * @param {string} data.kw
 */
export function getSearchResult(kw: string) {
  if (IS_CLIENT) {
    // 需要先用requestSearch通知客户端加载完成，再向客户端传递回调
    return Promise.race([
      callClient("setSearchResult"),
      callClient("requestSearch"),
    ]).then((res) => {
      if (res.content) {
        return JSON.parse(res.content);
      }
    });
  }
  return DEFAULT_PROMISE().then(({ mid, osbit, osVer }) => {
    return requestJsonp(URL.searchResult)({
      kw,
      osver: osVer,
      pack: osbit,
      mid,
    });
  });
}

/** 获取用户中心登录态 */
export function getUserLoginData() {
  return requestJsonp(URL.userLoginData)({
    src: "",
    from: "",
    charset: "UTF-8",
    requestScema: "https",
    o: "sso",
    m: "info",
    show_name_flag: "1",
    head_type: "q",
  }).then((res) => ({
    userID: res.qid as number,
    nickname: res.nickname as string,
    avatar: res.img_url as string,
    isLogin: !!res.qid,
    accountType: 1,
    sign: "",
  }));
}

/**
 * 获取用户登录态
 */
export function getLoginData(): Promise<ILoginData> {
  if (IS_CLIENT) {
    return Promise.all([getLoginInfo(), getUserHeadLocalImage()]).then(
      ([res, img]) => {
        return Object.assign({}, res.info, {
          avatar: img.base64data,
        });
      },
    );
  }
  return getUserLoginData();
}

export function getNewLoginData(): Promise<ILoginData> {
  if (IS_CLIENT) {
    return Promise.all([getLoginInfo()]).then(
      ([res]) => {
        return Object.assign({}, res.info);
      },
    );
  }
  return getUserLoginData();
}

/**
 * 获取用户积分和等级
 */
export function getUserLevel() {
  return requestJsonp(URL.userLevel)({
    _: getTimestamp(),
  });
}
/**
 * 获取用户签到状态
 */
export function getUserCheckInStatus() {
  return requestJsonp(URL.userCheckInStatus)({
    _: getTimestamp(),
  });
}

/**
 * 点击签到
 */
export function checkIn() {
  return requestJsonp(URL.checkIn)({
    _: getTimestamp(),
  });
}

/**
 * 获取商城订单列表
 * @param {Object} data
 * @param {string} data.status
 * @param {number} data.page
 * @param {number} data.length
 */
export function getMallList(status = "", page = 1, length = 20) {
  return request(URL.mallList)({
    p: page,
    n: length,
    status,
    _: getTimestamp(),
  });
}
export function queryFailOrder() {
  return request(URL.failOrder)({
    _: getTimestamp(),
  });
}
/**
 * 获取购买记录列表
 * @param {Object} data
 * @param {number} data.page
 * @param {number} data.length
 */
export function getPurchaseList(page = 1, length = 20) {
  return requestJsonp(URL.purchaseList)({
    start: page - 1, // 默认从0开始计数
    length,
    _: getTimestamp(),
  });
}

/**
 * 软件详情
 * @param {string} id
 */
export function getSoftInfo(id: string) {
  const url = `${URL.softInfo}softinfo_${id}.json`;
  return request(url)();
}

/**
 * 软件详情
 * @param {string} id
 */
export function getSoftBaikeInfo(name: string) {
  return requestJsonp(URL.softBaikeInfo)({
    name,
    c: "index",
    a: "findsoftinfo",
  });
}

/**
 * 获取软件评分
 * @param {string} ks
 */
export function getSoftPolls(ks: string) {
  return requestJsonp(URL.softPolls)({
    ks,
    cd: 1,
    ofmt: "json",
    ienc: "utf8",
  });
}

/**
 * 获取软件评分标签
 * @param {string} soft_id
 */
export function getPollsTag(soft_id: string) {
  return requestJsonp(URL.pollsTag)({
    soft_id,
  });
}

/**
 * 获取评论标签
 * @param {number} soft_id
 */
export function getCommentTag(soft_id: number) {
  return requestJsonp(URL.commentTag)({
    soft_id,
  });
}

/**
 * 我的评分
 * @param {string} soft_name
 */
export function getMyScore(soft_name: string) {
  return requestJsonp(URL.myScore)({
    name: soft_name,
  });
}

/**
 * 软件打分
 * @param {string} name
 * @param {number} qid
 * @param {number} aid
 */
export function addPoll({
  name,
  qid,
  aid,
}: {
  name: string;
  aid: number;
  qid: number;
}) {
  return requestJsonp(URL.pollsAdd)({
    name,
    aid,
    qid,
    charset: "utf8",
    c: "Poll",
    a: "addPoll",
  });
}

/**
 * 获取评论
 * @param {string} name
 * @param {string} type
 * @param {string} a
 * @param {number} start
 */
export function getComment(data: {
  name: string;
  type?: ICommentType;
  start?: number;
  a: "getmessage" | "getmessagenum" | "getHotList";
}) {
  return requestJsonp(URL.comment)({
    ...data,
    charset: "utf8",
    c: "message",
    count: 10,
    _: getTimestamp(),
  });
}

/**
 * 获取回复评论
 * @param {string} name
 * @param {string} type
 * @param {string} a
 * @param {number} page
 */
export function getReply(data: { name: string; msgid: string; page: number }) {
  return requestJsonp(URL.comment)({
    ...data,
    charset: "utf8",
    c: "message",
    a: "getreply",
    count: 5,
    _: getTimestamp(),
  });
}

/**
 * 点赞评论
 * @param {string} name
 * @param {string} msgid
 */
export function supportComment(data: { name: string; msgid: string }) {
  return requestJsonp(URL.comment)({
    ...data,
    charset: "utf8",
    c: "message",
    a: "addAgree",
    _: getTimestamp(),
  });
}

/**
 * 发表评论
 */
export function submitComment(data: {
  name: string;
  type: ICommentType;
  content: string;
  soft_id: number;
  tag_ids?: string; //  556|560|562
  tag_name?: string;
  msgid?: string;
}) {
  return requestJsonp(URL.comment)({
    ...data,
    charset: "utf8",
    c: "message",
    a: data.msgid ? "addreply" : "addmessage",
  });
}

/**
 * 获取商品信息
 * @param {Object} data
 * @param {string} item_id
 */
export function getItems(item_id: string) {
  return request(URL.mallItems)({
    item_id,
    _: getTimestamp(),
  });
}

/**
 * 获取新商品信息
 * @param {Object} data
 * @param {string} item_id
 */
export function getnewItems<T>(item_id: string) {
  return request<T>(URL.newmallItems)({
    spu_id: item_id,
    _: getTimestamp(),
  });
}

/**
 * 提交支付订单
 * @param {Object} option
 * @param {string} option.item_id
 * @param {string} option.property
 * @param {string} option.sid
 * @param {string} option.need_info
 */
export function submitOrder(data: {
  item_id: string;
  property: string;
  sid: string;
  need_info: string;
  [key: string]: string;
}) {
  return DEFAULT_PROMISE().then(({ mid }) => {
    const params = new URLSearchParams();
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        params.append(key, data[key]);
      }
    }
    params.append("_", getTimestamp().toString());
    params.append("mid", mid);
    return request(URL.submitOrder)(params, {
      method: "POST",
    });
  });
}

/**
 * 新版提交支付订单
 * @param {Object} option
 * @param {string} option.item_id
 * @param {string} option.property
 * @param {string} option.sid
 * @param {string} option.need_info
 */
export function newSubmitOrder(data: {
  sku_id: number;
  mid: string;
  phone?: string;
  email?: string;
  qq?: string;
  [key: string]: any;
}) {
  return DEFAULT_PROMISE().then(({ mid }) => {
    const params = new URLSearchParams();
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        params.append(key, data[key]);
      }
    }
    // params.append("_", getTimestamp().toString());
    params.append("mid", mid);
    return request(URL.newsubmitOrder)(params, {
      method: "POST",
    });
  });
}
/**
 * 查询订单状态
 * @param {String} order_id
 */
export function checkOrder(order_id: string) {
  return request(URL.checkOrder)({
    order_id,
    _: getTimestamp(),
  });
}

//新查询订单
export function checkNewOrder(order_id: string) {
  return request(URL.checkNewOrder)({
    order_id,
    _: getTimestamp(),
  });
}

// 查询sku
export function getGoodSku(sku_ids: string) {
  return request(URL.getGoodSku)({
    sku_ids,
    _: getTimestamp(),
  });
}

/**
 * 商城导航
 */
export function getMallNav() {
  return request(URL.mallNav)();
}

/**
 * 商城Banner
 */
export function getMallBanner() {
  return request(URL.mallBanner)();
}

/**
 * 商城热门推荐商品
 */
export function getMallHot() {
  return request(URL.mallHot)();
}

/**
 * 商城分类商品
 */
export function getMallCategory() {
  return request(URL.mallCategory)();
}

/**
 * 商城专题页
 * @param {number} cid
 */
export function getMallArea(cid: number) {
  return request(URL.mallArea)({
    cid,
  });
}

/**
 * 游戏数据
 */
export function getGameData() {
  return request(URL.gameData)({
    _: getTimestamp(),
  });
}

/* 软件榜单Banner */
export function getRankBanner() {
  return request(URL.rankBanner)({
    _: getTimestamp(),
  });
}

/* 软件榜单列表 */
export function getRankList() {
  return requestJsonp(URL.rankList)({
    _: getTimestamp(),
  });
}

/* 云游戏/页游 数据 */
export function getGameAreaData(type: "web" | "cloud") {
  switch (type) {
    case "web":
      return request(URL.webGameData)({
        _: getTimestamp(),
      });
      break;
    case "cloud":
      return request(URL.cloudGameData)({
        _: getTimestamp(),
      });

    default:
      return Promise.reject(new Error("请求参数错误"));
  }
}

/* 商业化接口 */

export function getPmpData(ids: string[]): Promise<IComData> {
  if (IS_CLIENT) {
    return DEFAULT_PROMISE().then(({ mid }) => {
      return requestJsonp2(URL.pmpUrl)(
        {
          showids: ids.join("_"),
          of: 4,
          newf: 2,
          type: 1,
          scheme: "https",
          mid,
          _: getTimestamp(),
        },
        {
          isCom: true,
          timeout: 3000,
        },
      );
    });
  }

  return requestJsonp2(URL.pmpUrl)(
    {
      showids: ids.join("_"),
      of: 4,
      newf: 2,
      type: 1,
      scheme: "https",
      mid: "test",
      _: getTimestamp(),
    },
    {
      isCom: true,
      timeout: 3000,
    },
  );
}

// 获取商业化showid
export function getShowIds() {
  return request(URL.showIds)();
}
